import React from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Upload, Loader2, Trash2, Send, Calendar, ChevronDown } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import ErrorBanner from '@/components/ErrorBanner'
import type { useTurbineDetailViewModel } from '../useTurbineDetailViewModel'

type VM = ReturnType<typeof useTurbineDetailViewModel>

interface CategoryPhotosSectionProps {
  turbine: NonNullable<VM['turbine']>
  isCustomer: VM['isCustomer']
  categoryPhotos: VM['categoryPhotos']
  selectedFiles: VM['selectedFiles']
  setSelectedFiles: VM['setSelectedFiles']
  uploading: VM['uploading']
  uploadError: VM['uploadError']
  setUploadError: VM['setUploadError']
  handleFileSelect: VM['handleFileSelect']
  handleUploadSelected: VM['handleUploadSelected']
  yearOptions: VM['yearOptions']
  dronePhotoYear: VM['dronePhotoYear']
  setDronePhotoYear: VM['setDronePhotoYear']
  operatorPhotoYear: VM['operatorPhotoYear']
  setOperatorPhotoYear: VM['setOperatorPhotoYear']
  robotPhotoYear: VM['robotPhotoYear']
  setRobotPhotoYear: VM['setRobotPhotoYear']
  selectedDroneBlade: VM['selectedDroneBlade']
  setSelectedDroneBlade: VM['setSelectedDroneBlade']
  selectedOperatorBlade: VM['selectedOperatorBlade']
  setSelectedOperatorBlade: VM['setSelectedOperatorBlade']
  selectedRobotBlade: VM['selectedRobotBlade']
  setSelectedRobotBlade: VM['setSelectedRobotBlade']
  isDroneYearDropdownOpen: VM['isDroneYearDropdownOpen']
  setIsDroneYearDropdownOpen: VM['setIsDroneYearDropdownOpen']
  isOperatorYearDropdownOpen: VM['isOperatorYearDropdownOpen']
  setIsOperatorYearDropdownOpen: VM['setIsOperatorYearDropdownOpen']
  isRobotYearDropdownOpen: VM['isRobotYearDropdownOpen']
  setIsRobotYearDropdownOpen: VM['setIsRobotYearDropdownOpen']
  isDroneBladeDropdownOpen: VM['isDroneBladeDropdownOpen']
  setIsDroneBladeDropdownOpen: VM['setIsDroneBladeDropdownOpen']
  isOperatorBladeDropdownOpen: VM['isOperatorBladeDropdownOpen']
  setIsOperatorBladeDropdownOpen: VM['setIsOperatorBladeDropdownOpen']
  isRobotBladeDropdownOpen: VM['isRobotBladeDropdownOpen']
  setIsRobotBladeDropdownOpen: VM['setIsRobotBladeDropdownOpen']
}

const CategoryPhotosSection: React.FC<CategoryPhotosSectionProps> = ({
  turbine,
  isCustomer,
  categoryPhotos,
  selectedFiles,
  setSelectedFiles,
  uploading,
  uploadError,
  setUploadError,
  handleFileSelect,
  handleUploadSelected,
  yearOptions,
  dronePhotoYear,
  setDronePhotoYear,
  operatorPhotoYear,
  setOperatorPhotoYear,
  robotPhotoYear,
  setRobotPhotoYear,
  selectedDroneBlade,
  setSelectedDroneBlade,
  selectedOperatorBlade,
  setSelectedOperatorBlade,
  selectedRobotBlade,
  setSelectedRobotBlade,
  isDroneYearDropdownOpen,
  setIsDroneYearDropdownOpen,
  isOperatorYearDropdownOpen,
  setIsOperatorYearDropdownOpen,
  isRobotYearDropdownOpen,
  setIsRobotYearDropdownOpen,
  isDroneBladeDropdownOpen,
  setIsDroneBladeDropdownOpen,
  isOperatorBladeDropdownOpen,
  setIsOperatorBladeDropdownOpen,
  isRobotBladeDropdownOpen,
  setIsRobotBladeDropdownOpen,
}) => {
  const navigate = useNavigate()
  const { t } = useLanguage()

  return (
    <>
            {/* Additional Photo Categories - Hidden for Customer */}
            {!isCustomer && (
            <div className="space-y-6 mt-8">
              {/* Drone Photos */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-r from-green-50 to-emerald-50/50 border border-green-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t.turbineDetail.photos.dronePhotos}
                    </h3>
                    {categoryPhotos.drone.length > 0 && (
                      <button
                        onClick={() => navigate(`/turbine/${turbine.id}/drone-photos`)}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      >
                        {t.turbineDetail.photos.viewPhotos}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    {/* Year Selection */}
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {t.turbineDetail.photos.year}:
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsDroneYearDropdownOpen(!isDroneYearDropdownOpen)}
                          className="bg-white border-2 border-green-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm font-medium text-left h-10 flex items-center justify-between min-w-[80px] hover:border-green-400 transition-colors"
                        >
                          <span className="text-gray-900">{dronePhotoYear}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ml-2 ${isDroneYearDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isDroneYearDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto">
                            {yearOptions.map(year => (
                              <button
                                key={year}
                                onClick={() => {
                                  setDronePhotoYear(year)
                                  setIsDroneYearDropdownOpen(false)
                                }}
                                className={`w-full px-4 py-2 text-left hover:bg-green-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                                  dronePhotoYear === year ? 'bg-green-100 text-green-700 font-medium' : 'text-gray-700'
                                }`}
                              >
                                {year}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Blade Selection */}
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">
                        Blade:
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsDroneBladeDropdownOpen(!isDroneBladeDropdownOpen)}
                          className="bg-white border-2 border-green-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm font-medium text-left h-10 flex items-center justify-between min-w-[80px] hover:border-green-400 transition-colors"
                        >
                          <span className="text-gray-900">Blade {selectedDroneBlade}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ml-2 ${isDroneBladeDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isDroneBladeDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999]">
                            {['A', 'B', 'C'].map(blade => (
                              <button
                                key={blade}
                                onClick={() => {
                                  setSelectedDroneBlade(blade)
                                  setIsDroneBladeDropdownOpen(false)
                                }}
                                className={`w-full px-4 py-2 text-left hover:bg-green-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                                  selectedDroneBlade === blade ? 'bg-green-100 text-green-700 font-medium' : 'text-gray-700'
                                }`}
                              >
                                Blade {blade}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/50 rounded-lg p-4">
                  {/* Upload Controls */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      {selectedFiles['drone'] && selectedFiles['drone'].length > 0 && !uploading['drone'] && (
                        <button
                          onClick={() => handleUploadSelected('drone')}
                          className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm font-medium"
                        >
                          <Send className="w-4 h-4 mr-1.5" />
                          {selectedFiles['drone'].length} Fotoğraf Yükle
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Photo Grid - Scrollable Layout */}
                  <div className="max-h-80 overflow-y-auto border rounded-lg p-4">
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                      {/* Fotoğraf Ekle Butonu - İlk Grid İtemi */}
                      <div className="upload-zone w-full aspect-square border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer transition-all duration-200 relative overflow-hidden border-green-300 hover:border-green-400 hover:bg-green-25">
                        <div 
                          className="text-center p-1 relative z-10 w-full h-full flex flex-col items-center justify-center"
                          onClick={() => document.getElementById('drone-upload')?.click()}
                        >
                          {uploading['drone'] ? (
                            <>
                              <Loader2 className="w-6 h-6 text-green-500 mb-1 animate-spin" />
                              <p className="text-xs text-green-600 font-medium">Yükleniyor</p>
                            </>
                          ) : (
                            <>
                              <Upload className="w-6 h-6 text-green-500 mb-1" />
                              <p className="text-xs text-green-600 font-medium">{t.turbineDetail.photos.addPhoto}</p>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Selected Files Preview */}
                      {selectedFiles['drone']?.map((file, index) => (
                        <div key={index} className="relative group w-full aspect-square">
                          <div className="w-full h-full bg-gray-100 rounded-lg overflow-hidden border hover:border-green-300 transition-colors">
                            <img 
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="w-full h-full object-cover cursor-pointer"
                            />
                            
                            {/* Remove button overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    const newFiles = (selectedFiles['drone'] || []).filter((_, i) => i !== index)
                                    setSelectedFiles(prev => ({ ...prev, drone: newFiles }))
                                  }}
                                  className="p-1 bg-white rounded-full text-red-600 hover:bg-red-50 transition-colors"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* File info tooltip on hover */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="truncate">{file.name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    

                  </div>
                  
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pcd"
                    className="hidden"
                    id="drone-upload"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        handleFileSelect('drone', files);
                      }
                    }}
                  />
                </div>
              </motion.div>

              {/* Operator Photos */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="bg-gradient-to-r from-purple-50 to-violet-50/50 border border-purple-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t.turbineDetail.photos.operatorPhotos}
                    </h3>
                    {categoryPhotos.operator.length > 0 && (
                      <button
                        onClick={() => navigate(`/turbine/${turbine.id}/operator-photos`)}
                        className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      >
                        {t.turbineDetail.photos.viewPhotos}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    {/* Year Selection */}
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {t.turbineDetail.photos.year}:
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsOperatorYearDropdownOpen(!isOperatorYearDropdownOpen)}
                          className="bg-white border-2 border-purple-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-medium text-left h-10 flex items-center justify-between min-w-[80px] hover:border-purple-400 transition-colors"
                        >
                          <span className="text-gray-900">{operatorPhotoYear}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ml-2 ${isOperatorYearDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isOperatorYearDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto">
                            {yearOptions.map(year => (
                              <button
                                key={year}
                                onClick={() => {
                                  setOperatorPhotoYear(year)
                                  setIsOperatorYearDropdownOpen(false)
                                }}
                                className={`w-full px-4 py-2 text-left hover:bg-purple-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                                  operatorPhotoYear === year ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-700'
                                }`}
                              >
                                {year}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Blade Selection */}
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">
                        Blade:
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsOperatorBladeDropdownOpen(!isOperatorBladeDropdownOpen)}
                          className="bg-white border-2 border-purple-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-medium text-left h-10 flex items-center justify-between min-w-[80px] hover:border-purple-400 transition-colors"
                        >
                          <span className="text-gray-900">Blade {selectedOperatorBlade}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ml-2 ${isOperatorBladeDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isOperatorBladeDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999]">
                            {['A', 'B', 'C'].map(blade => (
                              <button
                                key={blade}
                                onClick={() => {
                                  setSelectedOperatorBlade(blade)
                                  setIsOperatorBladeDropdownOpen(false)
                                }}
                                className={`w-full px-4 py-2 text-left hover:bg-purple-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                                  selectedOperatorBlade === blade ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-700'
                                }`}
                              >
                                Blade {blade}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/50 rounded-lg p-4">
                  {/* Upload Controls */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      {selectedFiles['operator'] && selectedFiles['operator'].length > 0 && !uploading['operator'] && (
                        <button
                          onClick={() => handleUploadSelected('operator')}
                          className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm font-medium"
                        >
                          <Send className="w-4 h-4 mr-1.5" />
                          {selectedFiles['operator'].length} Fotoğraf Yükle
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Photo Grid - Scrollable Layout */}
                  <div className="max-h-80 overflow-y-auto border rounded-lg p-4">
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                      {/* Fotoğraf Ekle Butonu - İlk Grid İtemi */}
                      <div className="upload-zone w-full aspect-square border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer transition-all duration-200 relative overflow-hidden border-purple-300 hover:border-purple-400 hover:bg-purple-25">
                        <div 
                          className="text-center p-1 relative z-10 w-full h-full flex flex-col items-center justify-center"
                          onClick={() => document.getElementById('operator-upload')?.click()}
                        >
                          {uploading['operator'] ? (
                            <>
                              <Loader2 className="w-6 h-6 text-purple-500 mb-1 animate-spin" />
                              <p className="text-xs text-purple-600 font-medium">Yükleniyor</p>
                            </>
                          ) : (
                            <>
                              <Upload className="w-6 h-6 text-purple-500 mb-1" />
                              <p className="text-xs text-purple-600 font-medium">{t.turbineDetail.photos.addPhoto}</p>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Selected Files Preview */}
                      {selectedFiles['operator']?.map((file, index) => (
                        <div key={index} className="relative group w-full aspect-square">
                          <div className="w-full h-full bg-gray-100 rounded-lg overflow-hidden border hover:border-purple-300 transition-colors">
                            <img 
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="w-full h-full object-cover cursor-pointer"
                            />
                            
                            {/* Remove button overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    const newFiles = (selectedFiles['operator'] || []).filter((_, i) => i !== index)
                                    setSelectedFiles(prev => ({ ...prev, operator: newFiles }))
                                  }}
                                  className="p-1 bg-white rounded-full text-red-600 hover:bg-red-50 transition-colors"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* File info tooltip on hover */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="truncate">{file.name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    

                  </div>
                  
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pcd"
                    className="hidden"
                    id="operator-upload"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        handleFileSelect('operator', files);
                      }
                    }}
                  />
                </div>
              </motion.div>

              {/* Robot Photos */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="bg-gradient-to-r from-orange-50 to-amber-50/50 border border-orange-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t.turbineDetail.photos.robotPhotos}
                    </h3>
                    {categoryPhotos.robot.length > 0 && (
                      <button
                        onClick={() => navigate(`/turbine/${turbine.id}/robot-photos`)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      >
                        {t.turbineDetail.photos.viewPhotos}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    {/* Year Selection */}
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {t.turbineDetail.photos.year}:
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsRobotYearDropdownOpen(!isRobotYearDropdownOpen)}
                          className="bg-white border-2 border-orange-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm font-medium text-left h-10 flex items-center justify-between min-w-[80px] hover:border-orange-400 transition-colors"
                        >
                          <span className="text-gray-900">{robotPhotoYear}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ml-2 ${isRobotYearDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isRobotYearDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto">
                            {yearOptions.map(year => (
                              <button
                                key={year}
                                onClick={() => {
                                  setRobotPhotoYear(year)
                                  setIsRobotYearDropdownOpen(false)
                                }}
                                className={`w-full px-4 py-2 text-left hover:bg-orange-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                                  robotPhotoYear === year ? 'bg-orange-100 text-orange-700 font-medium' : 'text-gray-700'
                                }`}
                              >
                                {year}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Blade Selection */}
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">
                        Blade:
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsRobotBladeDropdownOpen(!isRobotBladeDropdownOpen)}
                          className="bg-white border-2 border-orange-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm font-medium text-left h-10 flex items-center justify-between min-w-[80px] hover:border-orange-400 transition-colors"
                        >
                          <span className="text-gray-900">Blade {selectedRobotBlade}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ml-2 ${isRobotBladeDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isRobotBladeDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999]">
                            {['A', 'B', 'C'].map(blade => (
                              <button
                                key={blade}
                                onClick={() => {
                                  setSelectedRobotBlade(blade)
                                  setIsRobotBladeDropdownOpen(false)
                                }}
                                className={`w-full px-4 py-2 text-left hover:bg-orange-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                                  selectedRobotBlade === blade ? 'bg-orange-100 text-orange-700 font-medium' : 'text-gray-700'
                                }`}
                              >
                                Blade {blade}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/50 rounded-lg p-4">
                  {/* Upload Controls */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      {selectedFiles['robot'] && selectedFiles['robot'].length > 0 && !uploading['robot'] && (
                        <button
                          onClick={() => handleUploadSelected('robot')}
                          className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm font-medium"
                        >
                          <Send className="w-4 h-4 mr-1.5" />
                          {selectedFiles['robot'].length} Fotoğraf Yükle
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Photo Grid - Scrollable Layout */}
                  <div className="max-h-80 overflow-y-auto border rounded-lg p-4">
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                      {/* Fotoğraf Ekle Butonu - İlk Grid İtemi */}
                      <div className="upload-zone w-full aspect-square border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer transition-all duration-200 relative overflow-hidden border-orange-300 hover:border-orange-400 hover:bg-orange-25">
                        <div 
                          className="text-center p-1 relative z-10 w-full h-full flex flex-col items-center justify-center"
                          onClick={() => document.getElementById('robot-upload')?.click()}
                        >
                          {uploading['robot'] ? (
                            <>
                              <Loader2 className="w-6 h-6 text-orange-500 mb-1 animate-spin" />
                              <p className="text-xs text-orange-600 font-medium">Yükleniyor</p>
                            </>
                          ) : (
                            <>
                              <Upload className="w-6 h-6 text-orange-500 mb-1" />
                              <p className="text-xs text-orange-600 font-medium">{t.turbineDetail.photos.addPhoto}</p>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Selected Files Preview */}
                      {selectedFiles['robot']?.map((file, index) => (
                        <div key={index} className="relative group w-full aspect-square">
                          <div className="w-full h-full bg-gray-100 rounded-lg overflow-hidden border hover:border-orange-300 transition-colors">
                            <img 
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="w-full h-full object-cover cursor-pointer"
                            />
                            
                            {/* Remove button overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    const newFiles = (selectedFiles['robot'] || []).filter((_, i) => i !== index)
                                    setSelectedFiles(prev => ({ ...prev, robot: newFiles }))
                                  }}
                                  className="p-1 bg-white rounded-full text-red-600 hover:bg-red-50 transition-colors"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* File info tooltip on hover */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="truncate">{file.name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    

                  </div>
                  
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pcd"
                    className="hidden"
                    id="robot-upload"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        handleFileSelect('robot', files);
                      }
                    }}
                  />
                </div>
              </motion.div>
            </div>
            )}
            
            {/* Customer View-Only Photo Categories */}
            {isCustomer && (categoryPhotos.drone.length > 0 || categoryPhotos.operator.length > 0 || categoryPhotos.robot.length > 0) && (
              <div className="mt-8">
                <div className="flex flex-wrap justify-center gap-6">
                  {/* Drone Photos - View Only (only show if has photos) */}
                  {categoryPhotos.drone.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="group bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:border-green-300 w-full md:w-[calc(50%-12px)] xl:w-[calc(33.333%-16px)] max-w-sm"
                  >
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                        Drone Fotoğrafları
                      </h3>
                      <p className="text-sm text-gray-600">Havadan çekilen türbin fotoğrafları</p>
                    </div>

                    <button
                      onClick={() => navigate(`/turbine/${turbine.id}/drone-photos`)}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg group-hover:scale-[1.02]"
                    >
                      Fotoğrafları Görüntüle
                    </button>
                  </motion.div>
                  )}

                  {/* Operator Photos - View Only (only show if has photos) */}
                  {categoryPhotos.operator.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className="group bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:border-purple-300 w-full md:w-[calc(50%-12px)] xl:w-[calc(33.333%-16px)] max-w-sm"
                  >
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                        Operatör Fotoğrafları
                      </h3>
                      <p className="text-sm text-gray-600">Operatör tarafından çekilen fotoğraflar</p>
                    </div>

                    <button
                      onClick={() => navigate(`/turbine/${turbine.id}/operator-photos`)}
                      className="w-full bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg group-hover:scale-[1.02]"
                    >
                      Fotoğrafları Görüntüle
                    </button>
                  </motion.div>
                  )}

                  {/* Robot Photos - View Only (only show if has photos) */}
                  {categoryPhotos.robot.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="group bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:border-orange-300 w-full md:w-[calc(50%-12px)] xl:w-[calc(33.333%-16px)] max-w-sm"
                  >
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-orange-700 transition-colors">
                        Robot Fotoğrafları
                      </h3>
                      <p className="text-sm text-gray-600">Robot tarafından çekilen fotoğraflar</p>
                    </div>
                    <button
                      onClick={() => navigate(`/turbine/${turbine.id}/robot-photos`)}
                      className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg group-hover:scale-[1.02]"
                    >
                      Fotoğrafları Görüntüle
                    </button>
                  </motion.div>
                  )}
                </div>
              </div>
            )}
            
            {/* Upload Error -- ortak ErrorBanner: baslik + ikon + kapat, tek gorunum. */}
            {uploadError && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6"
              >
                <ErrorBanner message={uploadError} onDismiss={() => setUploadError(null)} />
              </motion.div>
            )}
    </>
  )
}

export default CategoryPhotosSection
